import { useCallback, useEffect, useState } from 'react';
import { apiErrMsg } from './util';
import type { DirtyReporter } from './types';

/* One tab's editable copy of part of a client, with the three things every tab
   form needs and used to reimplement: a dirty flag scoped to the keys it owns,
   a save that only fires when dirty, and a report of that dirtiness up to the
   page so a tab switch cannot drop an edit.

   `initial` is recomputed from the client on every render and used only for
   comparison — the draft is never reset from it. After a save the page
   invalidates the client, the new `initial` equals the draft, and the form
   reads as clean without any explicit reset. */
export function useDraft<T extends object>({
  formId, initial, keys, persist, onSaved, onDirtyChange,
}: {
  formId: string;
  initial: T;
  keys: readonly (keyof T)[];
  persist: (draft: T) => Promise<void>;
  onSaved?: () => void;
  onDirtyChange?: DirtyReporter;
}) {
  const [draft, setDraft] = useState<T>(initial);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const set = useCallback(<K extends keyof T>(k: K, v: T[K]) => {
    setDraft((prev) => ({ ...prev, [k]: v }));
  }, []);

  // JSON compare so nested values (team assignments, locations) count too.
  const dirty = keys.some((k) => JSON.stringify(draft[k]) !== JSON.stringify(initial[k]));

  useEffect(() => {
    onDirtyChange?.(formId, dirty);
    return () => onDirtyChange?.(formId, false);
  }, [formId, dirty, onDirtyChange]);

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    setErr(null);
    try {
      await persist(draft);
      onSaved?.();
    } catch (e) {
      // A 400 carries a message written for the person typing (a BigQuery table
      // id with spaces, say); show that rather than the raw "API 400: {…}".
      setErr(apiErrMsg(e, 'Could not save these settings.'));
    } finally {
      setSaving(false);
    }
  };

  return { draft, set, setDraft, dirty, saving, err, save };
}
